import React, { useState, useEffect } from "react";
import { Autocomplete, Chip, CircularProgress, TextField } from "@mui/material";
import axios from "axios";
const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#050d2e", // Set background color
    "& fieldset": {
      borderColor: "#050d2e", // default border color
    },
    "&:hover fieldset": {
      borderColor: "#000", // border color on hover
    },
    "&.Mui-focused fieldset": {
      borderColor: "#050d2e", // border color when focused
    },
  },
  "& input": {
    fontSize: "14px",
    color: "#fff", // Set entered text color
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8c99cb", // Set placeholder color
  },
};
interface MasterDataAutocomepleteProps {
  label: string;
  selectedValue: string | string[]; // Can be either string or string[]
  setSelectedValue: (value: string | string[]) => void;
  multiple?: boolean;
  type:
    | "skill"
    | "industry"
    | "jobTitle"
    | "tool"
    | "technology"
    | "education"
    | "certification";
  disableRenderTag?: boolean;
  isRequired?: boolean;
  onBlur?: any;
  fieldName?: string;
  valuesOptions?: any[];
  inputTextStyle?: any;
}

const inputStylesExt = {
  ...inputStyles,
  height: "42px",
  minHeight: "0px",
  "& .MuiOutlinedInput-root": {
    height: "42px",
    paddingLeft: 0.5,
    "&:hover fieldset": {
      borderColor: "transparent",
    },
    "&.Mui-focused fieldset": {
      borderColor: "transparent",
    },
  },
  "& input": {
    fontSize: "14px",
    color: "#fff !important",
    paddingLeft: "10px",
    lineHeight: "20px",
  },
  "& .MuiInputLabel-root": {
    color: "#b0b7c3",
  },
  "& .MuiAutocomplete-clearIndicator": {
    color: "#fff",
  },
  // ✅ Correct way to style the placeholder text
  "& input::placeholder": {
    color: "#b0b7c3 !important",
    opacity: 1, // Ensure it is fully visible
  },
};


const MasterDataAutoComplete: React.FC<MasterDataAutocomepleteProps> = ({
  label,
  type,
  selectedValue,
  setSelectedValue,
  multiple,
  disableRenderTag,
  isRequired,
  onBlur,
  fieldName,
  valuesOptions,
  inputTextStyle,
}) => {
  const token =
    "gAAAAABmrADSBIDN8FDuHY3lKMRXzkgtJj9q5m8znxap2HKPQ3XAl3CAnobCdKMA_G7fobmurSLsqdjTAn53WQzGcIO29TNWDhIl5rr4YqmdjlKzhCzQMD_li2SvHVtZ2W1D4LJCX6hY"; // Directly set the token here
  const [searchSkill, setSearchSkill] = useState<string>("");
  const [masterData, setMasterData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [focusedField, setFocusedField] = useState<
    "jobTitle" | "jobDescription" | "skills" | null
  >(null);

  const onSkillInputChange = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const inputValue = (event.target as HTMLInputElement).value;
    setSearchSkill(inputValue);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchSkill.trim() === "") {
        setMasterData([]);
        return;
      }

      setIsLoading(true);

      axios
        .post(
          "https://dev-services.eximius.ai/eximius/api/v2/get_masterdata",
          {
            page: 1,
            page_size: 20,
            search_keyword: searchSkill,
            types: [type],
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setMasterData(
            Array.isArray(response.data?.messageList?.[type])
              ? response.data?.messageList?.[type].map(
                  (list: any) => list?.value
                )
              : []
          );
        })
        .catch((error) => {
          console.error("Error fetching master data:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchSkill, type]);

  const handleInputChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedValue(newValue); // newValue is a string here, update the selected value immediately
  };

  return (
    <Autocomplete
      onBlur={onBlur}
      freeSolo // This allows the user to type their own value
      noOptionsText={isLoading ? "Loading..." : "No options"}
      id="jobIndustries"
      size="medium"
      data-testid={`auto${label}`}
      onChange={(_, newValue: any) => {
        setSelectedValue(newValue || ""); // This will set value when the user selects an item or types
      }}
      onInputChange={handleInputChange} // Correctly handle input change as a string
      options={Array.isArray(valuesOptions) ? valuesOptions : masterData || []}
      value={selectedValue} // Ensure the value is updated correctly
      getOptionLabel={(option: any) => option || ""}
      renderTags={(value, getTagProps) =>
        disableRenderTag
          ? null
          : value.map((option, index) => (
              <Chip
                color="primary"
                size="small"
                label={option}
                {...getTagProps({ index })}
              />
            ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          className="field-textbox"
          placeholder={label}
          onKeyUp={onSkillInputChange}
          required={isRequired}
          data-testid={label}
          variant="outlined"
          sx={inputTextStyle || inputStylesExt}
          onFocus={() => setFocusedField("jobTitle")}
          onBlur={() => setFocusedField(null)}
          
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && (
                  <CircularProgress sx={{ color: "#fff" }} size={20} />
                )}
                {selectedValue && params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MasterDataAutoComplete;